import { Suspense, lazy } from 'react';
import LoginRouterWrapper from '../LoginRouterWrapper';
import PageSkeleton from '../../shared/PageSkeleton';
import PasswordAndSettingsRoutes from '../../componentRoutesConfigs/PasswordAndSettingsRoutes/PasswordAndSettingsRoutes';
import SelectRoleRegistrationRoutes from '../../componentRoutesConfigs/RegistrationRoutes/RegistrationRoutes';
import ProtectedPageWrapper from '../../shared/ProtectedPageWrapper';
import PageSEO from '../../shared/PageSEO';

const MFAOneTimeCode = lazy(() => import('../MFAOneTimeCode/MFAOneTimeCode'));
const MFACodeValidation = lazy(() =>
	import('../MFACodeValidation/MFACodeValidation')
);

const ResetPasswordStepOne = lazy(() =>
	import('../ResetPassword/ResetPasswordStepOne')
);

const ResetPasswordStepTwo = lazy(() =>
	import('../ResetPassword/ResetPasswordStepTwo')
);

const RegistrationStep1 = lazy(() => import('../Registration/Step1'));

const ChangeAndEnforcePassword = lazy(() =>
	import('../ChangeAndEnforcePassword/ChangeAndEnforcePassword')
);
const EnforceSecurityQuestion = lazy(() =>
	import('../EnforceSecurityQuestion/EnforceSecurityQuestion')
);
const ForgotPassword = lazy(() => import('../ForgotPassword/ForgotPassword'));
const ForgotPasswordSuccess = lazy(() =>
	import('../ForgotPassword/ForgotPasswordSuccess')
);
const ForgotUsername = lazy(() => import('../ForgotUsername/ForgotUsername'));
const ForgotUsernameSuccess = lazy(() =>
	import('../ForgotUsername/ForgotUsernameSuccess')
);
const RegistrationStep2 = lazy(() => import('../Registration/Step2'));
const RegistrationExpiredToken = lazy(() =>
	import('../Registration/ExpiredToken/ExpiredToken')
);

const ChangeSecurityQuestion = lazy(() =>
	import('../ChangeSecurityQuestion/ChangeSecurityQuestion')
);
const PasswordAndSecurity = lazy(() =>
	import('../PasswordAndSecurity/PasswordAndSecurity')
);
const ChangeEmail = lazy(() => import('../ChangeEmail/ChangeEmail'));
const EmailVerified = lazy(() => import('../EmailVerified/EmailVerified'));
const MFAOnboarding = lazy(() => import('../MFAOnboarding/MFAOnboarding'));

/**
 * This function will return an array of identity routes to support websites that use createBrowserRouter.
 * @function generateIdentityRoutes
 * @param {Object} identityData An object that should have these properties:
 * 	apiEnvironment, ssId, redirectUri, clientId, customerServicePhoneNumber, ineligibleRegistration, eligibilityURL, enableSSO, logOutLink, path,
 *	showGenderField, blob, registrationFormLanguage, termsURL, eligibilityPrepopulation, ineligibleLanguage, duplicateCheckEnabled, enableEmailVerification,
 *	hasOwnProvider
 */

/*
Consuming app will use this array as the children object https://reactrouter.com/en/main/routers/create-browser-router 
*/

const loginElement = <LoginRouterWrapper />;
const enforceSQElement = <EnforceSecurityQuestion />;
const step2Element = <RegistrationStep2 />;

export const staticIdentityRoutes = [
	{
		path: 'login',
		children: [
			{
				index: true,
				element: loginElement,
			},
			{
				path: 'sso',
				element: loginElement,
			},
			{
				path: 'mfa',
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<MFAOneTimeCode />
					</Suspense>
				),
			},
			{
				path: 'mfaCodeValidation',
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<MFACodeValidation />
					</Suspense>
				),
			},
			{
				path: 'enforceSecurityQuestion',
				element: (
					<Suspense fallback={<PageSkeleton />}>{enforceSQElement}</Suspense>
				),
			},
			{
				path: 'enforceSecurityQuestion/sso',
				element: (
					<Suspense fallback={<PageSkeleton />}>{enforceSQElement}</Suspense>
				),
			},
			{
				path: 'enforcePassword',
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<ChangeAndEnforcePassword />
					</Suspense>
				),
			},
			{
				path: 'onboarding',
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<MFAOnboarding />
					</Suspense>
				),
			},
		],
	},
	{
		path: 'registration',
		children: [
			{
				index: true,
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<RegistrationStep1 />
					</Suspense>
				),
			},
			{
				path: 'step2',
				element: (
					<Suspense fallback={<PageSkeleton />}>{step2Element}</Suspense>
				),
			},
			{
				path: 'expiredToken',
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<RegistrationExpiredToken />
					</Suspense>
				),
			},
			{
				path: 'sso',
				element: step2Element,
			},
		],
	},
	{
		path: 'forgotPassword',
		children: [
			{
				index: true,
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<ForgotPassword />
					</Suspense>
				),
			},
			{
				path: 'success',
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<ForgotPasswordSuccess />
					</Suspense>
				),
			},
		],
	},
	{
		path: 'resetPassword',
		children: [
			{
				path: 'question',
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<ResetPasswordStepOne />
					</Suspense>
				),
			},
			{
				path: 'reset',
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<ResetPasswordStepTwo />
					</Suspense>
				),
			},
		],
	},
	{
		path: 'forgotUsername',
		children: [
			{
				index: true,
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<ForgotUsername />
					</Suspense>
				),
			},
			{
				path: 'success',
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<ForgotUsernameSuccess />
					</Suspense>
				),
			},
		],
	},
	{
		path: 'profile',
		component: <ProtectedPageWrapper />,
		children: [
			{
				path: 'security',
				children: [
					{
						index: true,
						element: (
							<Suspense fallback={<PageSkeleton />}>
								<PasswordAndSecurity />
							</Suspense>
						),
					},
					{
						path: 'securityQuestion',
						element: (
							<Suspense fallback={<PageSkeleton />}>
								<ChangeSecurityQuestion />
							</Suspense>
						),
					},
					{
						path: 'changePassword',
						element: (
							<Suspense fallback={<PageSkeleton />}>
								<ChangeAndEnforcePassword />
							</Suspense>
						),
					},
				],
			},
			{
				path: 'changeEmail',
				element: (
					<Suspense fallback={<PageSkeleton />}>
						<PageSEO pageTitle="Change Your Email" />
						<ChangeEmail />
					</Suspense>
				),
			},
		],
	},
	{
		path: 'emailVerification',
		element: (
			<Suspense fallback={<PageSkeleton />}>
				<EmailVerified />
			</Suspense>
		),
	},
	...PasswordAndSettingsRoutes,
	...SelectRoleRegistrationRoutes,
];
